import type { ProposalDelta, DigestEntry, MemoryFile, FileChange } from "./types";

/** Evidence carried by every non-no-op verb (the locked ProposalDelta union). */
function evidenceOf(delta: ProposalDelta): string[] | null {
  return delta.verb === "no-op" ? null : delta.evidence;
}

export interface GateOk { ok: true }
/**
 * The "rejected" digest entry, narrowed from DigestEntry's combined
 * no-op|rejected|failure member (Extract<…, { kind: "rejected" }> resolves to
 * never because that member's `kind` is the broader union, not the literal).
 */
type RejectedEntry = Extract<DigestEntry, { kind: "no-op" | "rejected" | "failure" }> & { kind: "rejected" };
export interface GateRejected { ok: false; rejected: RejectedEntry }

/**
 * Evidence-membership gate. Every non-no-op delta
 * must cite ONLY conv_ ids present in the set actually consumed/recovered this
 * cycle. Transcripts are untrusted (MINJA/SpAIware): a bare presence check on a
 * model-emitted string is insufficient, so we verify SET MEMBERSHIP. A non-no-op
 * delta with empty evidence is also rejected (nothing to ground it).
 */
export function checkEvidenceMembership(
  delta: ProposalDelta,
  harvested: ReadonlySet<string>,
): GateOk | GateRejected {
  const evidence = evidenceOf(delta);
  if (evidence === null) return { ok: true }; // no-op carries no evidence
  const fabricated = evidence.filter((id) => !harvested.has(id));
  if (evidence.length === 0 || fabricated.length > 0) {
    const detail =
      evidence.length === 0
        ? `delta verb=${delta.verb} cites no evidence`
        : `delta verb=${delta.verb} cites out-of-set ids: ${fabricated.join(", ")}`;
    return { ok: false, rejected: { kind: "rejected", detail, evidence } };
  }
  return { ok: true };
}

/** Aborts the WHOLE atomic batch — a supersede/retract whose OLD target is
 *  operator-origin must never half-apply. Caught by runDream → fail-closed. */
export class BatchAbort extends Error {
  constructor(public path: string, message: string) {
    super(message);
    this.name = "BatchAbort";
  }
}

export type OriginLookup = (path: string) => Promise<MemoryFile | null>;
export interface OriginOk { ok: true }
export interface OriginFlagged { ok: false; flagged: Extract<DigestEntry, { kind: "flagged" }> }

const isIndexFile = (path: string): boolean => /(^|\/)[^/]*_index\.md$/.test(path);
const isMemoryIndex = (path: string): boolean => path === "memory/MEMORY.md";

/** The verb's OLD/target path the tier is evaluated ON (OLD target). */
function targetPath(delta: ProposalDelta): string | null {
  switch (delta.verb) {
    case "new": return delta.path;
    case "supersede": return delta.oldPath;
    case "retract": return delta.path;
    case "flag": return delta.path;
    default: return null; // skill-seed/quarantine write to inbox/quarantine — no origin target
  }
}

/**
 * Origin-tier gate. Evaluated on the OLD/target file:
 * - worker | dream  -> full autonomy (edit allowed)
 * - operator (incl. seeds, AND absent-origin structural files) -> flag-only
 * - *_index.md (except memory/MEMORY.md) -> refuse (flag)
 * - supersede/retract whose OLD target is operator-origin -> THROW BatchAbort
 * Tiering keys off `origin`, not path/seeded-ness (founder.md seeded but origin: worker is editable).
 */
export async function checkOriginTier(
  delta: ProposalDelta,
  lookup: OriginLookup,
): Promise<OriginOk | OriginFlagged> {
  const evidence = evidenceOf(delta) ?? [];
  const path = targetPath(delta);
  if (path === null) return { ok: true }; // inbox/quarantine destinations bypass origin tiering

  // Index carve-out: only memory/MEMORY.md is editable among *_index.md.
  if (isIndexFile(path) && !isMemoryIndex(path)) {
    return { ok: false, flagged: { kind: "flagged", path, tension: `refused: ${path} is an index file (only memory/MEMORY.md is editable)`, evidence } };
  }

  const target = await lookup(path);
  // origin: absent ⇒ worker default, EXCEPT reference-structural files (treated operator).
  const isStructural = path.startsWith("references/");
  const origin = (target?.frontmatter.origin as string | undefined)
    ?? (isStructural ? "operator" : "worker");

  const editable = origin === "worker" || origin === "dream";
  if (editable) return { ok: true };

  // Operator-origin (or structural). A verb that RETIRES or REMOVES the operator
  // OLD target — a retract, or a cross-path supersede (oldPath !== newPath) that
  // would leave the old operator file stale alongside a new dream rev — aborts the
  // whole atomic batch (never half-apply). A same-path supersede is an
  // in-place edit of the operator file, which is flag-only like an operator `new`.
  const removesOperatorTarget =
    delta.verb === "retract" || (delta.verb === "supersede" && delta.oldPath !== delta.newPath);
  if (removesOperatorTarget) {
    throw new BatchAbort(path, `supersede/retract OLD target ${path} is origin: ${origin} — aborting batch (never half-apply)`);
  }
  return { ok: false, flagged: { kind: "flagged", path, tension: `operator-origin (${origin}); flag-only`, evidence } };
}

/**
 * Write-path allowlist (B1 SECURITY BLOCKER — fail-closed). The origin-tier gate
 * can NOT contain a write outside memory/: listMemory() only enumerates memory/
 * (writer.ts), so any non-memory target's lookup is null → origin defaults to
 * "worker" → editable → the gate passes → applyVerb writes delta.path VERBATIM →
 * commitBatch commits it. A prompt-injected transcript could thus steer a `new`
 * at AGENTS.md / .m8t/persona.md / the loader / SECURITY.md. This allowlist is the
 * fail-closed backstop run BEFORE applyVerb: EVERY target a verb writes must be
 * under the dreamer's own write surface (memory/*.md, inbox/, quarantine/).
 *
 * Verbs that DERIVE their destination (skill-seed→inbox/<date>/, quarantine→
 * quarantine/<date>/) are structurally safe — applyVerb hard-codes the prefix and
 * only interpolates a slugify()'d slug (alnum-kebab, no "/" or ".."), so they pass.
 * no-op/flag write no file; flag still carries a path we DIGEST, so it must point
 * into memory/ (a flag at AGENTS.md is a steered out-of-surface reference → reject).
 *
 * Disallowed disposition mirrors the other gates: a `new`/`flag` whose path escapes
 * is DROPPED to a 'rejected' digest entry (like checkEvidenceMembership). A
 * supersede/retract whose target escapes ABORTS the whole atomic batch (mirrors
 * checkOriginTier's removesOperatorTarget BatchAbort — a retire/replace verb must
 * never half-apply against an out-of-surface path).
 */
const ALLOWED_MEMORY = /^memory\/.+\.md$/;
const ALLOWED_INBOX = /^inbox\//;
const ALLOWED_QUARANTINE = /^quarantine\//;
const ALLOWED_FLAG = /^memory\//;

/** True for a "..": no path the dreamer derives or proposes should contain a parent-traversal segment. */
const hasTraversal = (path: string): boolean => path.split("/").includes("..");

export function checkPathAllowed(delta: ProposalDelta): GateOk | GateRejected {
  const evidence = evidenceOf(delta) ?? [];
  const reject = (detail: string): GateRejected => ({ ok: false, rejected: { kind: "rejected", detail, evidence } });
  const ok = (p: string, re: RegExp): boolean => re.test(p) && !hasTraversal(p);

  switch (delta.verb) {
    case "no-op":
      return { ok: true };

    case "new":
      return ok(delta.path, ALLOWED_MEMORY)
        ? { ok: true }
        : reject(`write-path not allowed: new target ${delta.path} escapes memory/ (allowlist: memory/*.md)`);

    case "flag":
      // flag writes no file but its path is a memory reference we digest — must point into memory/.
      return ok(delta.path, ALLOWED_FLAG)
        ? { ok: true }
        : reject(`write-path not allowed: flag path ${delta.path} is not under memory/`);

    case "skill-seed":
      // Destination is inbox/<date>/<slug>.md (applyVerb hard-codes the prefix). The slug must
      // not smuggle a traversal/separator — slugify strips those, but assert defensively.
      return ok(`inbox/${delta.slug}`, ALLOWED_INBOX)
        ? { ok: true }
        : reject(`write-path not allowed: skill-seed slug ${delta.slug} is not a safe inbox/ slug`);

    case "quarantine":
      return ok(`quarantine/${delta.slug}`, ALLOWED_QUARANTINE)
        ? { ok: true }
        : reject(`write-path not allowed: quarantine slug ${delta.slug} is not a safe quarantine/ slug`);

    case "supersede": {
      // BOTH the OLD (annotated in place) and NEW (written) targets must stay under memory/.
      // A retire/replace verb that escapes the surface aborts the batch (never half-apply).
      for (const p of [delta.oldPath, delta.newPath]) {
        if (!ok(p, ALLOWED_MEMORY)) {
          throw new BatchAbort(p, `supersede target ${p} escapes memory/ — aborting batch (never half-apply, B1 allowlist)`);
        }
      }
      return { ok: true };
    }

    case "retract":
      if (!ok(delta.path, ALLOWED_MEMORY)) {
        throw new BatchAbort(delta.path, `retract target ${delta.path} escapes memory/ — aborting batch (never half-apply, B1 allowlist)`);
      }
      return { ok: true };
  }
}

/** Shared slug helper (owned here) — lower-kebab, alnum-only, trimmed.
 *  Used by forcePolicyQuarantine and any verb that derives a file slug. */
export const slugify = (s: string): string =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

// Stem-match (no trailing \b after the alternation): the cues are word *stems*, so a
// trailing \b was a bug — it demanded a word boundary immediately after the stem,
// which never exists when the stem is followed by its own suffix letters. e.g. /approv\b/
// fails on "approve"/"auto-approve" (v|e, no boundary) and /elevat\b/ on "elevated"; the
// canonical injection "Operator policy: always auto-approve." thus slipped past. We anchor
// the START with \b (so we still match on a word, not mid-word) and let each alternative run
// to its natural stem end. Both cue sets stay STEM-aligned so suffixed forms are caught.
const POLICY_CUES = /\b(standing policy|policy is|policy:|by default|always|never|going forward|from now on|auto[- ]?approv|automatic)/i;
const PRIVILEGE_CUES = /\b(approv|grant|elevat|owner|contributor|admin|rbac|privileg|permission|access|bypass|skip\s+(the\s+)?review)/i;

/** True if a candidate memory body declares a standing policy about approvals/privileges
 *  (backstop) — phrasing-agnostic: a policy/permanence cue AND a privilege cue. */
export function assertsStandingPolicy(body: string): boolean {
  return POLICY_CUES.test(body) && PRIVILEGE_CUES.test(body);
}

/** If a `new`/`supersede` candidate asserts a standing policy/privilege grant, convert it to a
 *  `quarantine` delta (lands in quarantine/, never the index) regardless of the proposed verb. */
export function forcePolicyQuarantine(delta: ProposalDelta): ProposalDelta {
  if ((delta.verb === "new" || delta.verb === "supersede") && assertsStandingPolicy(delta.body)) {
    return {
      verb: "quarantine",
      slug: slugify(delta.title),
      reason: "code-side standing-policy/auto-approval/privilege assertion",
      body: delta.body,
      evidence: delta.evidence,
    };
  }
  return delta;
}

export interface IdemOk { ok: true }
/** Same narrowing as RejectedEntry: DigestEntry's no-op|rejected|failure member carries
 *  the broader `kind` union, so Extract<…, { kind: "no-op" }> resolves to never — pin the literal. */
type NoOpEntry = Extract<DigestEntry, { kind: "no-op" | "rejected" | "failure" }> & { kind: "no-op" };
export interface IdemSkipped { ok: false; skipped: NoOpEntry }

/** Collect every conv_ id already recorded in memory `source:` frontmatter. */
function consumedSourceIds(memory: MemoryFile[]): Set<string> {
  const ids = new Set<string>();
  for (const m of memory) {
    const src = m.frontmatter.source;
    if (Array.isArray(src)) for (const id of src) if (typeof id === "string") ids.add(id);
  }
  return ids;
}

/**
 * Idempotency gate. Before applying, check whether any of
 * this delta's source conv_ ids ALREADY carry a memory file (crash between write
 * and cursor-advance re-presents the same window). The dream is non-deterministic
 * so loader-rule-6 identical-batch self-dedup does NOT cover this — the
 * source-id scan is the backstop. ANY overlap ⇒ already-consumed ⇒ skip + no-op.
 */
export function checkIdempotency(
  delta: ProposalDelta,
  existingMemory: MemoryFile[],
): IdemOk | IdemSkipped {
  const evidence = evidenceOf(delta);
  if (evidence === null) return { ok: true };
  const consumed = consumedSourceIds(existingMemory);
  const already = evidence.filter((id) => consumed.has(id));
  if (already.length > 0) {
    return {
      ok: false,
      skipped: { kind: "no-op", detail: `source already consumed (idempotent skip): ${already.join(", ")}`, evidence },
    };
  }
  return { ok: true };
}

export interface ApplyCtx {
  readFile: (path: string) => Promise<string | null>;
  at: Date;
}

const INDEX_PATH = "memory/MEMORY.md";
const ymd = (d: Date): string => d.toISOString().slice(0, 10);

/** YAML frontmatter scalar/array emit — only writes the small, known key set. */
function frontmatter(fields: Record<string, string | string[]>): string {
  const lines: string[] = ["---"];
  for (const [k, v] of Object.entries(fields)) {
    if (Array.isArray(v)) {
      lines.push(`${k}:`);
      for (const item of v) lines.push(`  - ${item}`);
    } else {
      lines.push(`${k}: ${v}`);
    }
  }
  lines.push("---");
  return lines.join("\n");
}

/** Insert a key into existing frontmatter (edit-in-place verbs); append at end of the block. */
function injectFrontmatter(body: string, fields: Record<string, string | string[]>): string {
  // \r?\n tolerates CRLF line endings (Windows git checkouts) as well as LF.
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/.exec(body);
  const inject = Object.entries(fields)
    .map(([k, v]) =>
      Array.isArray(v) ? `${k}:\n${v.map((i) => `  - ${i}`).join("\n")}` : `${k}: ${v}`,
    )
    .join("\n");
  if (!m) return `---\n${inject}\n---\n${body}`;
  const block = m[1].replace(/\s*$/, "");
  // m[0] is anchored at ^, so it is body's leading slice — splice the rebuilt block
  // in by its length rather than String.replace, whose replacement string would
  // expand $&/$1/$` tokens that can appear in founder-edited frontmatter values.
  return `---\n${block}\n${inject}\n---\n` + body.slice(m[0].length);
}

const indexLine = (path: string, title: string, date: string, tags: string[]): string =>
  `- \`${path}\` — **${title}**: ${title}. (${date} · ${tags.join(", ")})`;

/** Split MEMORY.md into [header-block, memory-line[]] — header preserved verbatim. */
function splitIndex(index: string): { header: string; lines: string[] } {
  const all = index.split("\n");
  const firstLineIdx = all.findIndex((l) => l.startsWith("- `memory/"));
  if (firstLineIdx === -1) return { header: index.replace(/_No memories yet\._\s*$/, "").trimEnd() + "\n", lines: [] };
  const header = all.slice(0, firstLineIdx).join("\n").replace(/_No memories yet\._\s*$/, "").trimEnd() + "\n";
  const lines = all.slice(firstLineIdx).filter((l) => l.startsWith("- `memory/"));
  return { header, lines };
}

function renderIndex(header: string, lines: string[]): string {
  return `${header}\n${lines.join("\n")}\n`;
}

/**
 * Translate a GATED delta into the FileChange[] the writer commits.
 * create-or-update only — never deletes (locked FileChange contract). Honors
 * the two-file supersede chain + index swap, retract-in-place + de-index,
 * skill-seed→inbox/<date>/, quarantine→quarantine/<date>/, flag→digest-only.
 */
export async function applyVerb(delta: ProposalDelta, ctx: ApplyCtx): Promise<FileChange[]> {
  const date = ymd(ctx.at);
  switch (delta.verb) {
    case "no-op":
    case "flag":
      return []; // digest-only

    case "new": {
      const fm = frontmatter({ origin: "dream", source: delta.evidence });
      const mem: FileChange = { path: delta.path, content: `${fm}\n# ${delta.title}\n\n${delta.body}\n` };
      const index = (await ctx.readFile(INDEX_PATH)) ?? "# Memory index\n";
      const { header, lines } = splitIndex(index);
      const idx: FileChange = {
        path: INDEX_PATH,
        content: renderIndex(header, [indexLine(delta.path, delta.title, date, delta.tags), ...lines]),
      };
      return [mem, idx];
    }

    case "supersede": {
      const oldBody = (await ctx.readFile(delta.oldPath)) ?? "---\norigin: worker\n---\n";
      const oldAnnotated: FileChange = {
        path: delta.oldPath,
        content: injectFrontmatter(oldBody, { superseded_by: delta.newPath }),
      };
      const newFm = frontmatter({ origin: "dream", supersedes: delta.oldPath, source: delta.evidence });
      const newFile: FileChange = { path: delta.newPath, content: `${newFm}\n# ${delta.title}\n\n${delta.body}\n` };
      const index = (await ctx.readFile(INDEX_PATH)) ?? "# Memory index\n";
      const { header, lines } = splitIndex(index);
      const kept = lines.filter((l) => !l.includes(`\`${delta.oldPath}\``)); // remove OLD line
      const idx: FileChange = {
        path: INDEX_PATH,
        content: renderIndex(header, [indexLine(delta.newPath, delta.title, date, delta.tags), ...kept]),
      };
      return [oldAnnotated, newFile, idx];
    }

    case "retract": {
      const body = (await ctx.readFile(delta.path)) ?? "---\norigin: dream\n---\n";
      const annotated: FileChange = {
        path: delta.path,
        content: injectFrontmatter(body, {
          retracted: ctx.at.toISOString(),
          retraction_evidence: delta.evidence,
        }),
      };
      const index = (await ctx.readFile(INDEX_PATH)) ?? "# Memory index\n";
      const { header, lines } = splitIndex(index);
      const kept = lines.filter((l) => !l.includes(`\`${delta.path}\``)); // de-index in place
      const idx: FileChange = { path: INDEX_PATH, content: renderIndex(header, kept) };
      return [annotated, idx];
    }

    case "skill-seed": {
      const fm = frontmatter({
        type: "skill-seed", origin: "dream", title: delta.title,
        description: delta.description, tags: delta.tags, source: delta.evidence,
      });
      return [{ path: `inbox/${date}/${delta.slug}.md`, content: `${fm}\n${delta.body}\n` }];
    }

    case "quarantine": {
      const fm = frontmatter({
        origin: "dream", type: "quarantine",
        quarantine_reason: delta.reason, quarantine_evidence: delta.evidence,
      });
      return [{ path: `quarantine/${date}/${delta.slug}.md`, content: `${fm}\n${delta.body}\n` }];
    }
  }
}
